import { PageSurface } from "../PageBody";
import { styled } from "goober";
import { usePendingRequestCount } from "../../hooks/usePendingRequestCount";
import { useMakeUrl, routes, useCurrentLocation } from "../../lib/urls";
import { Backdrop } from "../Backdrop";
import { CountBadge } from "../CountBadge";
import { ResponsiveTabs } from "../ResponsiveTabs";
import { PanelHeader, PanelHeaderNavigation } from "../PanelHeader";
import { MainScrollContainer } from "../MainScrollContainer";
import { Text } from "../Text";

const Container = styled(PageSurface)`
  flex: 1;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 16px;
  box-shadow: var(--elevation-low);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled(PanelHeader)`
  padding: 0;
  gap: 0;

  &[data-divider="true"] {
    border-bottom: 0.5px solid var(--border-alpha);
  }
`;

const Tabs = styled(PanelHeaderNavigation)`
  padding: 12px;
`;

const TABS = [
  {
    route: routes.monitoring.workflows,
    label: "Workflows",
    icon: "product-workflows",
  },
  {
    route: routes.monitoring.connectors,
    label: "Connectors",
    icon: "api-key-custom",
  },
  { route: routes.monitoring.access, label: "Access map", icon: "orbit" },
  { route: routes.monitoring.value, label: "Savings", icon: "wallet" },
  { route: routes.monitoring.ai, label: "AI", icon: "brain" },
  {
    route: routes.monitoring.requests,
    label: "Requests",
    icon: "heart-handshake",
  },
] as const;

type Props = {
  children: React.ReactNode;
  ready: boolean;
  headerItem?: React.ReactNode;
  // Draws a hairline under the tab strip. Full-bleed list tabs (Requests) want it
  // to separate the sticky list header from the tabs; the carded dashboard tabs
  // don't, so it's opt-in.
  headerDivider?: boolean;
};

export function MonitoringLayout({
  children,
  ready,
  headerItem,
  headerDivider,
}: Props) {
  const pendingCount = usePendingRequestCount();
  const makeUrl = useMakeUrl();
  const path = useCurrentLocation().split("?")[0];
  const items = TABS.map(({ route, label, icon }) => ({
    key: label,
    label,
    icon,
    to: makeUrl(route),
    badge:
      route === routes.monitoring.requests && pendingCount > 0 ? (
        <CountBadge>
          <Text variant="text-smaller-md">{pendingCount}</Text>
        </CountBadge>
      ) : undefined,
  }));
  const activeKey =
    items.find((item) => item.to.split("?")[0] === path)?.key ?? "Workflows";
  return (
    <Backdrop permanentNavigation>
      <Container>
        <Header data-divider={headerDivider}>
          <Tabs>
            <ResponsiveTabs
              label="Monitoring views"
              items={items}
              activeKey={activeKey}
            />
          </Tabs>
          {headerItem}
        </Header>
        <MainScrollContainer ready={ready}>{children}</MainScrollContainer>
      </Container>
    </Backdrop>
  );
}
